import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Shield, CheckCircle, Star, TrendingUp, Clock, Award, MessageCircle, Users, Target, Zap, ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import LPHeader from '../components/LPHeader';
import LPFooter from '../components/LPFooter';
import InquiryModal from '../components/InquiryModal';
import PricingSection from '../components/PricingSection';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { countryCodes } from '../utils/countryCodes';

// Counter Hook
const useCounter = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime;
    const isPercentage = end.toString().includes('%');
    const isPlus = end.toString().includes('+');
    const numericEnd = parseInt(end.toString().replace(/[^\d]/g, ''));
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const currentCount = Math.floor(progress * numericEnd);
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);
  
  const suffix = end.toString().includes('%') ? '%' : end.toString().includes('+') ? '+' : '';
  return count + suffix;
};

// Stat Counter Component
const StatCounter = ({ number, label, startCounter }) => {
  const displayNumber = useCounter(number, 2000, startCounter);
  
  return (
    <div className="text-center">
      <div className="text-5xl md:text-6xl font-bold text-[#D72638] mb-2">{displayNumber}</div>
      <div className="text-lg md:text-xl font-bold text-[#2B0A12]">{label}</div>
    </div>
  );
};

// Testimonials Slider Component
const TestimonialsSlider = () => {
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start' }, [
    Autoplay({ delay: 3000, stopOnInteraction: false })
  ]);

  const testimonials = [
    { name: 'Sarah Mitchell', role: 'eCommerce Store Owner', service: 'Trademark', content: 'They made trademark registration so easy! Filed my application within 48 hours and kept me updated throughout. My brand is now federally protected.' },
    { name: 'Michael Rodriguez', role: 'Tech Startup Founder', service: 'Copyright', content: 'Professional copyright service for our software. The attorney team was knowledgeable and responsive. Highly recommend for any creative business.' },
    { name: 'Emily Chen', role: 'Fashion Designer', service: 'Trademark', content: 'Best decision I made for my fashion brand. The $45 offer was incredible value, and the service was top-notch. Now my designs are fully protected.' },
    { name: 'David Thompson', role: 'Restaurant Owner', service: 'Company Formation', content: 'Helped us form our LLC and register our restaurant trademark. Everything was handled professionally and quickly. Great team!' },
    { name: 'Jennifer Lopez', role: 'Fitness Coach', service: 'Patent', content: 'They guided us through patent application for our fitness equipment. Complex process made simple by their expert attorneys. Worth every penny.' }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-4">What Our Clients Say</h2>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0">
                <Card className="p-6 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-2 hover:border-[#FFB400] border-2 border-transparent group cursor-pointer">
                  <div className="flex space-x-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-[#FFB400] text-[#FFB400] group-hover:scale-110 transition-transform duration-300" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic leading-relaxed text-sm">"{testimonial.content}"</p>
                  <div className="border-t pt-3">
                    <h4 className="font-semibold text-[#2B0A12] group-hover:text-[#D72638] transition-colors duration-300">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-xs text-[#D72638] font-medium mt-1">{testimonial.service} Service</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', brandName: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState('US');
  const [geoData, setGeoData] = useState(null);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState('');
  const [tmSignUpType, setTmSignUpType] = useState(1);
  const [packageInfo, setPackageInfo] = useState({ title: 'Basic', amount: 45 });
  const [startCounter, setStartCounter] = useState(false);
  const statsRef = useRef(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Fetch geolocation on component mount to detect user's country
  useEffect(() => {
    const fetchGeoLocation = async () => {
      try {
        const { getGeoLocation, getCountryCodeFromGeo } = await import('../services/leadApi');
        const geoLocationData = await getGeoLocation();
        
        if (geoLocationData?.isSuccess) {
          setGeoData(geoLocationData);
          const countryInfo = getCountryCodeFromGeo(geoLocationData);
          setCountryCode(countryInfo.countryCode);
          setDetectedCountry(countryInfo.country);
          console.log('Detected country:', countryInfo.country, 'Code:', countryInfo.countryCode);
        }
      } catch (error) {
        console.error('Failed to fetch geolocation:', error);
      }
    };
    
    fetchGeoLocation();
  }, []);

  // Email validation - standard email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - exactly 10 digits after country code
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/[^\d]/g, '');
    return digitsOnly.length === 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if all required fields are filled
    if (!formData.name || !formData.email || !formData.phone || !formData.brandName) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please check the agreement box to continue');
      return;
    }

    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter exactly 10 digits after country code';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      // Import lead API dynamically
      const { submitLead } = await import('../services/leadApi');
      
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: detectedCountry,
        description: formData.brandName
      };

      // Use pre-fetched geoData instead of fetching again
      const result = await submitLead(leadData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Application submitted successfully!');
        // Redirect will be handled automatically by submitLead
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (context, tmType = null, pkgInfo = null) => {
    setModalContext(context);
    if (tmType !== null) {
      setTmSignUpType(tmType);
    }
    if (pkgInfo !== null) {
      setPackageInfo(pkgInfo);
    }
    setIsModalOpen(true);
  };

  const handleTmSignUpTypeChange = (tmType) => {
    setTmSignUpType(tmType);
  };

  // Intersection Observer for counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStartCounter(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Start Your Trademark Registration Today - Fast & Affordable Protection</title>
        <meta name="description" content="Protect your brand now! Expert trademark registration starting at $49. Quick filing, professional support. Secure your business identity today." />
      </Helmet>
      <LPHeader onOpenModal={handleOpenModal} />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#FFB400] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D72638] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mobile/Tablet: Content appears second, Desktop: Content appears first */}
            <div className="order-2 lg:order-1">
              <div className="mb-6">
                <span className="inline-block bg-[#FFB400] text-[#2B0A12] font-extrabold text-sm px-6 py-2 rounded-full uppercase tracking-widest shadow-lg">
                  ⚡ Special Launch Price
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 lg:mb-6 leading-tight">
                Federal Trademark Registration Starting at Just <span className="text-[#FFB400]">$45</span>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-gray-300 mb-6 lg:mb-8 leading-relaxed">
                Protect your brand identity with comprehensive trademark registration services. Our expert legal team ensures your intellectual property gets the protection it deserves. Join thousands of satisfied clients who trust us with their brand security.
              </p>
            </div>

            {/* Mobile/Tablet: Form appears first, Desktop: Form appears second */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8 order-1 lg:order-2">
              
              <div className="text-center mb-6">
                <div className="inline-block bg-gradient-to-r from-[#D72638] to-[#2B0A12] text-white font-bold px-6 py-2 rounded-full mb-3 shadow-md">
                  🎯 Get Started Today
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#2B0A12] mb-2">Start Your Trademark Application</h3>
                <p className="text-gray-600">Fill out the form below for a free consultation</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                
                <div>
                  <Input type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} onBlur={() => { if (formData.email && !validateEmail(formData.email)) { setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' })); } else { setErrors(prev => ({ ...prev, email: '' })); }}} className={errors.email ? 'border-red-500' : ''} required />
                  {errors.email && <p className="text-red-500 text-sm mt-1">⚠️ {errors.email}</p>}
                </div>

                <div>
                  <div className="flex gap-2">
                    <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-24 px-2 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium">
                      {countryCodes.map((country, index) => (<option key={index} value={country.code}>{country.flag} {country.code}</option>))}
                    </select>
                    <Input 
                      type="tel" 
                      placeholder="123-456-7890 *" 
                      value={formData.phone} 
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d\s\-]/g, '');
                        const digitsOnly = value.replace(/[^\d]/g, '');
                        if (digitsOnly.length <= 10) {
                          setFormData({...formData, phone: value});
                        }
                      }} 
                      onBlur={() => { 
                        if (formData.phone && !validatePhone(formData.phone)) { 
                          setErrors(prev => ({ ...prev, phone: 'Must be exactly 10 digits' })); 
                        } else { 
                          setErrors(prev => ({ ...prev, phone: '' })); 
                        }
                      }} 
                      className={`flex-1 ${errors.phone ? 'border-red-500' : ''}`} 
                      required 
                      maxLength={12}
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-sm mt-1">⚠️ {errors.phone}</p>}
                  <p className="text-xs text-gray-500 mt-1">Format: 123-456-7890 (exactly 10 digits)</p>
                </div>

                <Textarea 
                  placeholder="Describe your brand/trademark and how you plan to use it *" 
                  value={formData.brandName} 
                  onChange={(e) => setFormData({...formData, brandName: e.target.value})} 
                  required 
                  rows={3}
                  className="resize-none"
                />

                <div className="flex items-start space-x-2">
                  <input type="checkbox" id="agree-lp" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-1 h-4 w-4 text-[#D72638] cursor-pointer" />
                  <label htmlFor="agree-lp" className="text-xs text-gray-600 cursor-pointer">I agree to receive communications from Trademark Essential.</label>
                </div>

                <Button type="submit" disabled={isLoading} className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold py-4 text-lg rounded-lg shadow-xl transition-all duration-300 disabled:opacity-50">
                  {isLoading ? 'Submitting...' : 'Submit Application'}
                </Button>
                <p className="text-center text-xs text-gray-500">✓ No Obligation • ✓ Secure & Confidential</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mobile: Image appears first, Desktop: Image on left */}
            <div className="relative h-[300px] md:h-[400px] lg:h-[450px] rounded-2xl overflow-hidden shadow-2xl order-1 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_brand-protection-1/artifacts/if74d0h1_img-1.jpg" 
                alt="Professional Business and Brand Protection" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B0A12]/40 to-transparent"></div>
            </div>

            {/* Mobile: Content appears second, Desktop: Content on right */}
            <div className="order-2 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2B0A12] mb-6">Secure Your Brand Identity Today</h2>
              <div className="space-y-5">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  In today's competitive marketplace, your brand is your most valuable asset. A registered trademark provides exclusive legal rights to your business name, logo, and slogan across the United States.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Without trademark protection, competitors can legally use similar names that confuse your customers and dilute your brand reputation. Registration establishes your ownership, increases business value, and gives you powerful legal tools to defend your identity.
                </p>
                <div className="mt-6 p-5 bg-[#FFB400]/10 border-l-4 border-[#FFB400] rounded-lg">
                  <p className="text-base md:text-lg text-[#2B0A12] font-semibold">
                    Every day without protection is a risk to your business. Secure your trademark rights before someone else claims your brand name.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

     
      

      {/* Pricing Section */}
      <PricingSection onOpenModal={handleOpenModal} onTmSignUpTypeChange={handleTmSignUpTypeChange} />

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-[#2B0A12] to-[#3D1C24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Why Businesses Choose Us</h2>
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
              We combine legal expertise with modern technology to deliver fast, affordable, and reliable trademark registration services. Here\'s what sets us apart from the competition.
            </p>
          </div>

          {/* Desktop: Grid layout, Mobile: Slider */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Licensed Legal Experts', desc: 'Our team of experienced trademark attorneys reviews every application to ensure maximum approval chances. With 15+ years of combined experience, we know exactly what the USPTO looks for and how to position your application for success.' },
              { icon: Zap, title: 'Lightning-Fast Processing', desc: 'Most trademark services take weeks to file your application. We process submissions within 24-48 hours, getting your brand protected faster. Our streamlined system eliminates delays while maintaining the highest quality standards.' },
              { icon: Target, title: '98% Success Rate Guarantee', desc: 'We are confident in our process - so confident that we offer a money-back guarantee. With a 98% approval rate and thousands of satisfied clients, you can trust us to protect your brand. If we cannot register your trademark, you pay nothing.' }
            ].map((item, index) => (
              <Card key={index} className="p-8 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border-2 border-transparent hover:border-[#FFB400] group cursor-pointer">
                <div className="w-16 h-16 bg-[#FFB400] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <item.icon className="h-8 w-8 text-[#2B0A12]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-[#FFB400] transition-colors duration-300">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.desc}</p>
              </Card>
            ))}
          </div>

          {/* Mobile: Slider */}
          <div className="md:hidden overflow-hidden">
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
              {[
                { icon: Shield, title: 'Licensed Legal Experts', desc: 'Our team of experienced trademark attorneys reviews every application to ensure maximum approval chances. With 15+ years of combined experience, we know exactly what the USPTO looks for and how to position your application for success.' },
                { icon: Zap, title: 'Lightning-Fast Processing', desc: 'Most trademark services take weeks to file your application. We process submissions within 24-48 hours, getting your brand protected faster. Our streamlined system eliminates delays while maintaining the highest quality standards.' },
                { icon: Target, title: '98% Success Rate Guarantee', desc: 'We are confident in our process - so confident that we offer a money-back guarantee. With a 98% approval rate and thousands of satisfied clients, you can trust us to protect your brand. If we cannot register your trademark, you pay nothing.' }
              ].map((item, index) => (
                <Card key={index} className="flex-shrink-0 w-80 p-6 bg-white/10 backdrop-blur-sm text-white shadow-xl border-2 border-transparent snap-center">
                  <div className="w-12 h-12 bg-[#FFB400] rounded-full flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-[#2B0A12]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section ref={statsRef} className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B0A12] mb-2">Trusted by Thousands of Businesses</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCounter number="5000+" label="Trademarks Successfully Registered" startCounter={startCounter} />
            <StatCounter number="98%" label="Approval Success Rate" startCounter={startCounter} />
            <StatCounter number="15+" label="Years of Combined Experience" startCounter={startCounter} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 md:py-16 lg:py-20 bg-gradient-to-r from-[#2B0A12] via-[#D72638] to-[#2B0A12] overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_brand-protection-1/artifacts/b6xwbi01_cta-bg.jpg')] bg-cover bg-center opacity-20"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 lg:mb-6">Don't Wait-Protect Your Brand Today</h2>
          <p className="text-sm md:text-base lg:text-lg text-white/90 mb-6 lg:mb-8 leading-relaxed">
            Every day you wait is another day competitors can steal your brand name. Join thousands of smart business owners who\'ve secured their trademark rights. Get started now with our exclusive $45 offer and safeguard your business future.
          </p>
          <div className="px-4">
            <Button 
              onClick={() => handleOpenModal('CTA Section - Protect Brand')} 
              className="w-full max-w-md mx-auto bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold px-6 sm:px-8 md:px-10 py-4 md:py-5 text-base md:text-lg rounded-lg shadow-2xl hover:shadow-[0_20px_60px_rgba(255,180,0,0.5)] transition-all duration-300 hover:scale-105 hover:-translate-y-1"
            >
              Start My Trademark Registration for $45
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-[#FFB400] uppercase tracking-wide mb-2">
              Got Questions?
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-4">Frequently Asked Questions</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about our trademark registration services
            </p>
          </div>

          <div className="space-y-4">
            {[
              { q: 'How long does trademark registration take?', a: 'The entire trademark registration process typically takes 8-12 months from filing to final approval. However, we file your application within 24-48 hours of receiving your information. The majority of time is spent in the USPTO review queue, which we actively monitor and manage for you.' },
              { q: 'What\'s included in the $45 trademark registration offer?', a: 'Our $45 offer includes a basic trademark search, filing assistance, document review, and submission to the USPTO. This is perfect for straightforward trademark applications. For comprehensive attorney-prepared applications with full legal review, we recommend our Standard or Professional packages.' },
              { q: 'Can I trademark a name that\'s already being used?', a: 'It depends. If someone else has already registered the same or similar trademark in your industry, you likely cannot use it. That\'s why we conduct a comprehensive trademark search before filing. If a conflict exists, we\'ll notify you and suggest alternatives to avoid rejection and wasted filing fees.' },
              { q: 'Do I really need a trademark, or can I just use my business name?', a: 'While you can use any business name without registration, only a registered trademark gives you exclusive nationwide rights and legal protection. Without registration, competitors can use similar names, causing customer confusion and damaging your brand. A trademark also increases business value and credibility.' },
              { q: 'What if my trademark application gets rejected?', a: 'We maintain a 98% approval rate, but if your application faces rejection, our attorneys will respond to office actions and work to overcome objections. Our Standard and higher packages include free office action responses. If approval is ultimately impossible, we offer a money-back guarantee on select packages.' }
            ].map((faq, index) => (
              <Card 
                key={index} 
                className={`overflow-hidden border-2 transition-all duration-300 ${
                  openFAQ === index 
                    ? 'border-[#FFB400] shadow-xl bg-gradient-to-r from-[#FFB400]/5 to-transparent' 
                    : 'border-gray-200 hover:border-[#FFB400] hover:shadow-lg'
                }`}
              >
                <button 
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)} 
                  className="w-full p-6 text-left flex items-center justify-between group transition-all duration-300"
                >
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openFAQ === index 
                        ? 'bg-[#FFB400] text-[#2B0A12]' 
                        : 'bg-gray-100 text-gray-600 group-hover:bg-[#FFB400] group-hover:text-[#2B0A12]'
                    }`}>
                      <span className="font-bold text-sm">{index + 1}</span>
                    </div>
                    <h3 className={`text-base md:text-lg font-bold pr-4 transition-colors duration-300 ${
                      openFAQ === index 
                        ? 'text-[#D72638]' 
                        : 'text-[#2B0A12] group-hover:text-[#D72638]'
                    }`}>
                      {faq.q}
                    </h3>
                  </div>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openFAQ === index 
                      ? 'bg-[#D72638] rotate-180' 
                      : 'bg-gray-100 group-hover:bg-[#FFB400]'
                  }`}>
                    <ChevronDown className={`h-5 w-5 transition-all duration-300 ${
                      openFAQ === index 
                        ? 'text-white' 
                        : 'text-gray-600 group-hover:text-[#2B0A12]'
                    }`} />
                  </div>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 animate-in slide-in-from-top-2 duration-300">
                    <div className="pl-12 border-l-4 border-[#FFB400] ml-4">
                      <p className="text-gray-700 leading-relaxed pl-4">{faq.a}</p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      <LPFooter />
      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={modalContext} tmSignUpType={tmSignUpType} packageInfo={packageInfo} />
    </div>
  );
};

export default LandingPage;